import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { publicationService } from '../services/publication'
import type {
  Publication,
  PublicationCategory,
  PublicationFilterParams,
  PublicationStats,
  ApiResponse,
  PaginatedResponse,
  PaginationParams,
  CreatePublication,
  UpdatePublication
} from '../types'

// Query keys
export const publicationKeys = {
  all: ['publications'] as const,
  lists: () => [...publicationKeys.all, 'list'] as const,
  list: (params: PaginationParams, filters: PublicationFilterParams) =>
    [...publicationKeys.lists(), params, filters] as const,
  details: () => [...publicationKeys.all, 'detail'] as const,
  detail: (id: string) => [...publicationKeys.details(), id] as const,
  stats: () => [...publicationKeys.all, 'stats'] as const,
  categories: () => [...publicationKeys.all, 'categories'] as const,
}

// Hook to fetch publications with pagination and filters
export function usePublications(
  params: PaginationParams = { page: 1, size: 50 },
  filters: PublicationFilterParams = {}
) {
  return useQuery<ApiResponse<PaginatedResponse<Publication>>, Error>({
    queryKey: publicationKeys.list(params, filters),
    queryFn: () => publicationService.getPublications(params, filters),
  })
}

// Hook to fetch publication stats
export function usePublicationStats() {
  return useQuery<PublicationStats, Error>({
    queryKey: publicationKeys.stats(),
    queryFn: async () => {
      const response = await publicationService.getPublicationStats()
      return response.data
    },
  })
}

// Hook to fetch a single publication by ID
export function usePublication(publicationId: string) {
  return useQuery<ApiResponse<Publication>, Error>({
    queryKey: publicationKeys.detail(publicationId),
    queryFn: () => publicationService.getPublicationById(publicationId),
    enabled: !!publicationId,
  })
}

// Hook to create a publication
export function useCreatePublication() {
  const queryClient = useQueryClient()

  return useMutation<ApiResponse<Publication>, Error, CreatePublication>({
    mutationFn: (data: CreatePublication) =>
      publicationService.createPublication(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: publicationKeys.lists() })
      queryClient.invalidateQueries({ queryKey: publicationKeys.stats() })
    },
  })
}

// Hook to update a publication
export function useUpdatePublication() {
  const queryClient = useQueryClient()

  return useMutation<ApiResponse<Publication>, Error, { publicationId: string; data: UpdatePublication }>({
    mutationFn: ({ publicationId, data }) =>
      publicationService.updatePublication(publicationId, data),
    onSuccess: (_, { publicationId }) => {
      queryClient.invalidateQueries({ queryKey: publicationKeys.detail(publicationId) })
      queryClient.invalidateQueries({ queryKey: publicationKeys.lists() })
      queryClient.invalidateQueries({ queryKey: publicationKeys.stats() })
    },
  })
}

// Hook to delete a publication
export function useDeletePublication() {
  const queryClient = useQueryClient()

  return useMutation<ApiResponse<null>, Error, string>({
    mutationFn: (publicationId: string) =>
      publicationService.deletePublication(publicationId),
    onSuccess: () => {
      // Invalidate and refetch publications list
      queryClient.invalidateQueries({ queryKey: publicationKeys.lists() })
      queryClient.invalidateQueries({ queryKey: publicationKeys.stats() })
    },
  })
}

// Hook to fetch publication categories
export function usePublicationCategories() {
  return useQuery<ApiResponse<PublicationCategory[]>, Error>({
    queryKey: publicationKeys.categories(),
    queryFn: () => publicationService.getPublicationCategories(),
  })
}
